import React, { Fragment, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import moment from "moment";
import ContentLoader from "../../components/loader/ContentLoader";
import { formatSupplierInfo } from "../../helper/commonHelper";
import { inventoriesService } from "../../services/inventories.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";
import {
  Avatar,
  Badge,
  DETAIL_ACCENTS,
  DetailBlock,
  DetailGroup,
  StatStrip,
  EmptyState as Empty,
  HeroChip,
  Pill,
  RouteHero,
  detailLabelStyle,
  detailTableWrap,
  detailTdStyle,
  detailThStyle,
  isEmpty,
  show,
  titleCase,
} from "../../components/common/DetailUI";

const money = (v) =>
  isEmpty(v) ? "-" : `₹ ${Number(v).toLocaleString("en-IN")}`;
const timeText = (t) => (t ? String(t).slice(0, 5) : "-");
const dateText = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");
const dateTimeText = (d) =>
  d ? moment(d).format("DD MMM YYYY, hh:mm A") : "-";
const changeValue = (v) =>
  v === null || v === undefined || v === ""
    ? "—"
    : typeof v === "object"
      ? JSON.stringify(v)
      : String(v);

const TRIP_TYPES = {
  one_way: "One Way",
  O: "One Way",
  round_trip: "Round Trip",
  R: "Round Trip",
};

const ACCENTS = {
  summary: DETAIL_ACCENTS.indigo,
  flight: DETAIL_ACCENTS.sky,
  segments: DETAIL_ACCENTS.violet,
  fares: DETAIL_ACCENTS.green,
  history: DETAIL_ACCENTS.amber,
};

const thStyle = { ...detailThStyle, padding: "8px 10px" };
const tdStyle = { ...detailTdStyle, padding: "8px 10px" };

const InventoryDetails = () => {
  const { id } = useParams();
  const location = useLocation();

  // the list the user came from (my inventory or all inventory)
  const backTo = location.state?.from || "/inventory/all-inventory-list";
  const backLabel =
    backTo === "/inventory/list" ? "Inventory" : "All Inventory";

  // the list row carries display names (cities, airline, supplier) the details API does not return
  const listRow =
    location.state?.inventory?.inventory_id == id
      ? location.state.inventory
      : null;

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openHistory, setOpenHistory] = useState({});

  useEffect(() => {
    loadInventory();
  }, [id]);

  const loadInventory = async () => {
    try {
      setLoading(true);
      setDetails(null);
      const res = await inventoriesService.details(id);
      if (res?.success) {
        setDetails(res.resData);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      withReactContent(Swal).fire({
        title: "Error",
        text: SOMETHING_WENT_WRONG,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleHistory = (hid) =>
    setOpenHistory((prev) => ({ ...prev, [hid]: !prev[hid] }));

  const header = (
    <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
      <div>
        <h4
          className="m-0 fw-bold"
          style={{ color: "var(--page-heading)", fontSize: 20 }}
        >
          Inventory Details
        </h4>
        <nav
          className="d-flex align-items-center gap-1 mt-1"
          style={{ fontSize: 12, color: "#475569" }}
        >
          <Link to="/dashboard" style={{ color: "#475569" }}>
            <i className="bi bi-house-door"></i>
          </Link>
          <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
          <Link to={backTo} style={{ color: "#475569" }}>
            {backLabel}
          </Link>
          <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
          <span className="fw-semibold" style={{ color: "#4a5568" }}>
            {details?.inventory?.inventory_code || `Inventory #${id}`}
          </span>
        </nav>
      </div>
      <Link
        to={backTo}
        className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
        style={{
          background: "var(--btn-bg)",
          borderRadius: 8,
          height: 34,
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <i className="bi bi-list-ul"></i> {backLabel} List
      </Link>
    </div>
  );

  if (loading) return <ContentLoader loaderType="circleSpin" />;

  if (!details?.inventory) {
    return (
      <section className="section dashboard">
        {header}
        <Empty text="Inventory not found" />
      </section>
    );
  }

  const inv = details.inventory;
  const stops = details.flight_stops || [];
  const pnrs = details.pnr_details || [];
  const history = details.history || [];

  const totalSeats = pnrs.reduce(
    (sum, p) => sum + Number(p.seat_quantity || 0),
    0,
  );
  const bookedSeats = pnrs.reduce(
    (sum, p) => sum + Number(p.booked_quantity || 0),
    0,
  );
  const fares = pnrs
    .map((p) => Number(p.total_amount))
    .filter((n) => !isNaN(n));
  const lowestFare = fares.length ? Math.min(...fares) : null;

  const depCity =
    listRow?.dep_airport_code == inv.departure_airport_code
      ? listRow?.dep_city_name
      : null;
  const arrCity =
    listRow?.arr_airport_code == inv.arrival_airport_code
      ? listRow?.arr_city_name
      : null;
  const airlineText = listRow?.airline_name
    ? `${listRow.airline_name} (${inv.airline_code})`
    : inv.airline_code;

  const stats = [
    { icon: "bi-grid-3x3-gap-fill", label: "Total Seats", value: totalSeats, accent: "#6366f1" },
    { icon: "bi-person-check-fill", label: "Booked", value: bookedSeats, accent: "#0ea5e9" },
    { icon: "bi-check2-circle", label: "Available", value: totalSeats - bookedSeats, accent: "#10b981" },
    { icon: "bi-tag-fill", label: "Lowest Fare", value: money(lowestFare), accent: "#f59e0b" },
  ];

  return (
    <section className="section dashboard">
      {header}

      <RouteHero
        chips={
          <>
            <HeroChip icon="bi-upc-scan">{inv.inventory_code}</HeroChip>
            <HeroChip icon="bi-airplane">
              {airlineText} · {inv.flight_number}
            </HeroChip>
          </>
        }
        right={
          <>
            <HeroChip>
              {TRIP_TYPES[inv.trip_type] || titleCase(inv.trip_type)}
            </HeroChip>
            <Badge value={inv.status} />
          </>
        }
        from={{
          code: inv.departure_airport_code,
          city: depCity,
          time: timeText(inv.departure_time),
          sub: `Terminal ${show(inv.departure_terminal)}`,
        }}
        to={{
          code: inv.arrival_airport_code,
          city: arrCity,
          time: timeText(inv.arrival_time),
          sub: `Terminal ${show(inv.arrival_terminal)}`,
        }}
        middle={
          inv.flight_stops == 0
            ? "Non-stop"
            : `${inv.flight_stops} stop${inv.flight_stops > 1 ? "s" : ""}`
        }
      />

      <StatStrip stats={stats} />

      {/* Overview: three grouped lists side by side */}
          <div className="row g-3 mb-3">
          <div className="col-12 col-lg-4">
                <DetailGroup
                  icon="bi-journal-text"
                  title="Inventory Details"
                  accent={ACCENTS.history}
                  rows={[
                    { label: "Inventory Code", value: inv.inventory_code },
                    { label: "Inventory ID", value: inv.id },
                    { label: "Status", value: inv.status, badge: true },
                    { label: "Bulk Upload File", value: inv.upload_file_id },
                    { label: "Created At", value: dateTimeText(inv.created_at) },
                    { label: "Modified At", value: dateTimeText(inv.modified_at) },
                    { label: "Remarks", value: inv.remarks, hideIfEmpty: true },
                  ]}
                />
              </div>
              <div className="col-12 col-lg-4">
                <DetailGroup
                  icon="bi-airplane-fill"
                  title="Flight & Fare Rules"
                  accent={ACCENTS.flight}
                  rows={[
                    { label: "Airline", value: airlineText },
                    { label: "Flight Number", value: inv.flight_number },
                    { label: "Trip Type", value: TRIP_TYPES[inv.trip_type] || titleCase(inv.trip_type) },
                    { label: "RBD", value: inv.rbd },
                    { label: "Fare Code", value: inv.fare_code },
                    { label: "Baggage Policy", value: inv.baggage_policy },
                    { label: "Fare Rule", value: inv.fare_rule==='non_refundable_non_changable' ? "Non-refundable / Non-changeable" : inv.fare_rule, hideIfEmpty: true },
                  ]}
                />
              </div>
              <div className="col-12 col-lg-4">
                <DetailGroup
                  icon="bi-building"
                  title="Supplier Details"
                  accent={ACCENTS.summary}
                  rows={[
                    { label: "Supplier", value: listRow?.supplier_name },
                    { label: "Supplier Code", value: inv.supplier_code },
                    { label: "Supplier Type", value: titleCase(inv.supplier_type) },
                    {
                      label: "Supplier Info",
                      value: inv.supplier_info ? formatSupplierInfo(inv.supplier_info) : null,
                      hideIfEmpty: true,
                    },
                    { label: "Hide Before (Hours)", value: `${inv.hr} Hours` },
                  ]}
                />
              </div>              
          </div>

          <DetailBlock icon="bi-signpost-split" title="Flight Segments" count={stops.length} accent={ACCENTS.segments}>
            {stops.length > 0 ? (
              <div className="table-responsive" style={detailTableWrap}>
                <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["Segment", "From", "Dep Time", "Dep Terminal", "To", "Arr Time", "Arr Terminal", "Status"].map((h) => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stops.map((s, i) => (
                      <tr key={s.id ?? i}>
                        <td style={tdStyle}>
                          <Pill value={Number(s.stop_number) + 1} accent={ACCENTS.segments} />
                        </td>
                        <td style={tdStyle} className="fw-bold">{show(s.departure_airport_code)}</td>
                        <td style={tdStyle}>{timeText(s.departure_time)}</td>
                        <td style={tdStyle}>{show(s.departure_terminal)}</td>
                        <td style={tdStyle} className="fw-bold">{show(s.arrival_airport_code)}</td>
                        <td style={tdStyle}>{timeText(s.arrival_time)}</td>
                        <td style={tdStyle}>{show(s.arrival_terminal)}</td>
                        <td style={tdStyle}><Badge value={s.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty text="No flight segments" />
            )}
          </DetailBlock>

          <DetailBlock icon="bi-cash-stack" title="PNR & Fares" count={pnrs.length} accent={ACCENTS.fares}>
            {pnrs.length > 0 ? (
              <div className="table-responsive" style={detailTableWrap}>
                <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["PNR", "Dates", "Seats (Booked / Total)", "Basic", "YQ", "YR", "OT", "Infant", "Markup", "Total", "Status"].map((h) => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pnrs.map((p, i) => {
                      const available = Number(p.seat_quantity || 0) - Number(p.booked_quantity || 0);
                      return (
                        <tr key={p.id ?? i}>
                          <td style={tdStyle}>
                            <div className="fw-bold">{show(p.pnr)}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>#{show(p.id)}</div>
                          </td>
                          <td style={tdStyle}>
                            <div>{dateText(p.departure_date)}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8" }}>→ {dateText(p.arrival_date)}</div>
                          </td>
                          <td style={tdStyle}>
                            <Pill value={`${show(p.booked_quantity)} / ${show(p.seat_quantity)}`} accent="#6366f1" />
                            <span className="ms-2" style={{ fontSize: 11, color: available > 0 ? "#059669" : "#dc2626", fontWeight: 600 }}>
                              {available} left
                            </span>
                          </td>
                          <td style={tdStyle}>{money(p.basic_amount)}</td>
                          <td style={tdStyle}>{money(p.yq_amount)}</td>
                          <td style={tdStyle}>{money(p.yr_amount)}</td>
                          <td style={tdStyle}>{money(p.ot_amount)}</td>
                          <td style={tdStyle}>{money(p.infant_amount)}</td>
                          <td style={tdStyle}>{money(p.markup1)}</td>
                          {/* <td style={tdStyle}>{money(p.markup2)}</td> */}
                          <td style={{ ...tdStyle, color: "#047857" }} className="fw-bold">{money(p.total_amount)}</td>
                          <td style={tdStyle}><Badge value={p.status} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty text="No PNR details" />
            )}
          </DetailBlock>

          <DetailBlock icon="bi-clock-history" title="History" count={history.length} accent={ACCENTS.history}>
            {history.length > 0 ? (
              <div className="table-responsive" style={detailTableWrap}>
                <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      {["Date", "Event", "Action", "PNR Row", "Remarks", "Changed By", "Source", "Changes"].map((h) => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => {
                      const hid = h.id ?? i;
                      const changes = Object.entries(h.changes || {});
                      const isOpen = !!openHistory[hid];
                      return (
                        <Fragment key={hid}>
                          <tr>
                            <td style={tdStyle}>
                              <div className="fw-semibold">{dateText(h.created_at)}</div>
                              <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                {h.created_at ? moment(h.created_at).format("hh:mm A") : ""}
                              </div>
                            </td>
                            <td style={tdStyle}><Badge value={h.event} /></td>
                            <td style={tdStyle} className="text-capitalize">{show(h.action)}</td>
                            <td style={{ ...tdStyle, color: "#94a3b8" }}>
                              {isEmpty(h.inventory_pnr_id) ? "-" : `#${h.inventory_pnr_id}`}
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: "normal", minWidth: 220 }}>{show(h.remarks)}</td>
                            <td style={tdStyle}>
                              <div className="d-flex align-items-center gap-2">
                                <Avatar name={h.changedBy?.name} accent={ACCENTS.summary} />
                                <div>
                                  <div className="fw-semibold text-capitalize">{show(h.changedBy?.name)}</div>
                                  {h.changedBy?.code && (
                                    <div style={{ fontSize: 11, color: "#94a3b8" }}>{h.changedBy.code}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={tdStyle} className="text-capitalize">{show(h.source)}</td>
                            <td style={tdStyle}>
                              {changes.length > 0 ? (
                                <button
                                  type="button"
                                  className="btn btn-sm d-flex align-items-center gap-1"
                                  style={{ background: ACCENTS.history + "1a", color: "#b45309", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600 }}
                                  onClick={() => toggleHistory(hid)}
                                >
                                  <i className={"bi " + (isOpen ? "bi-chevron-up" : "bi-chevron-down")}></i>
                                  {changes.length} field{changes.length > 1 ? "s" : ""}
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={8} style={{ background: "#fffbeb", padding: "12px 16px" }}>
                                <div className="row g-2">
                                  {changes.map(([field, val]) => (
                                    <div key={field} className="col-12 col-md-6 col-lg-4">
                                      <div style={{ background: "#fff", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 10px" }}>
                                        <div style={{ ...detailLabelStyle, marginBottom: 2 }}>{titleCase(field)}</div>
                                        <div style={{ fontSize: 13 }}>
                                          <span style={{ color: "#b91c1c", textDecoration: "line-through", opacity: 0.8 }}>
                                            {changeValue(val?.from)}
                                          </span>
                                          <i className="bi bi-arrow-right mx-2" style={{ color: "#94a3b8" }}></i>
                                          <span className="fw-semibold" style={{ color: "#15803d" }}>
                                            {changeValue(val?.to)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty text="No history" />
            )}
          </DetailBlock>
    </section>
  );
};

export default InventoryDetails;
